import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  Upload, 
  FileText, 
  Calendar, 
  User,
  TrendingUp,
  Clock,
  CheckCircle2,
  BarChart3
} from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StatCard = ({ title, value, icon: Icon, description, trend, color }) => (
    <Card className="glass-card hover:shadow-lg transition-all">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium" style={{ color: '#64748B' }}>
          {title}
        </CardTitle>
        <div 
          className="p-2 rounded-lg"
          style={{ backgroundColor: `${color}20` }}
        >
          <Icon className="h-5 w-5" style={{ color }} />
        </div>
      </CardHeader>
      <CardContent>
        <div className="text-3xl font-bold mb-1" style={{ color: '#245451' }}>
          {value}
        </div>
        {description && (
          <p className="text-xs flex items-center gap-1" style={{ color: '#84a2ac' }}>
            {trend && <TrendingUp className="h-3 w-3" />}
            {description}
          </p>
        )}
      </CardContent>
    </Card>
  );

const DashboardPage = () => {
  const [stats, setStats] = useState({
    totalCases: 0,
    completedCases: 0,
    recentCases: [],
    summariesThisMonth: 0
  });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await axios.get(`${API}/cases`);
      const cases = response.data;
      
      const completed = cases.filter(c => c.status === 'completed').length;
      const recent = cases.slice(0, 5);
      
      // Calculate summaries this month
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const summariesThisMonth = cases.filter(c => {
        const createdAt = new Date(c.created_at);
        return createdAt >= startOfMonth && c.status === 'completed';
      }).length;

      setStats({
        totalCases: cases.length,
        completedCases: completed,
        recentCases: recent,
        summariesThisMonth
      });
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2" style={{ color: '#FFFFFF' }}>
            Dashboard
          </h1>
          <p className="text-lg" style={{ color: '#F5F7FA' }}>
            Medical records & billing analysis overview
          </p>
        </div>

        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#245451' }}></div>
            <p className="mt-4" style={{ color: '#F5F7FA' }}>Loading dashboard...</p>
          </div>
        ) : (
          <>
            {/* Stats Grid */}
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4 mb-8">
              <StatCard
                title="Total Cases"
                value={stats.totalCases}
                icon={FileText}
                description="All time"
                color="#245451"
              />
              <StatCard
                title="Completed"
                value={stats.completedCases}
                icon={CheckCircle2}
                description="Summaries ready"
                color="#C49603"
              />
              <StatCard
                title="This Month"
                value={stats.summariesThisMonth}
                icon={BarChart3}
                description="Summaries generated"
                trend
                color="#406962"
              />
              <StatCard
                title="Active"
                value={stats.totalCases - stats.completedCases}
                icon={Clock}
                description="In progress"
                color="#84a2ac"
              />
            </div>

            {/* Main Content Grid */}
            <div className="grid gap-6 lg:grid-cols-3">
              {/* Upload New Records Card */}
              <Card className="glass-card hover:shadow-lg transition-all">
                <CardHeader>
                  <div className="flex items-center space-x-3 mb-2">
                    <div 
                      className="p-2 rounded-lg"
                      style={{ backgroundColor: 'rgba(36, 84, 81, 0.15)' }}
                    >
                      <Upload className="h-6 w-6" style={{ color: '#245451' }} />
                    </div>
                    <CardTitle style={{ color: '#245451' }}>Upload New Records</CardTitle>
                  </div>
                  <CardDescription style={{ color: '#64748B' }}>
                    Start a new case by uploading medical records and bills
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button 
                    onClick={() => navigate('/upload')}
                    className="w-full"
                    style={{ backgroundColor: '#245451' }}
                  >
                    <Upload className="mr-2 h-4 w-4" />
                    Create New Case
                  </Button>
                  <div className="mt-4 text-xs" style={{ color: '#84a2ac' }}>
                    <p>Accepted files:</p>
                    <p className="mt-1">PDF, JPEG, PNG, DOCX, XLSX</p>
                  </div>
                </CardContent>
              </Card>

              {/* Recent Uploads Card */}
              <Card className="glass-card lg:col-span-2">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div 
                        className="p-2 rounded-lg"
                        style={{ backgroundColor: 'rgba(196, 150, 3, 0.15)' }}
                      >
                        <Clock className="h-6 w-6" style={{ color: '#C49603' }} />
                      </div>
                      <CardTitle style={{ color: '#245451' }}>Recent Cases</CardTitle>
                    </div>
                    <Button 
                      variant="outline"
                      size="sm"
                      onClick={() => navigate('/cases')}
                      style={{ borderColor: '#E2E8F0', color: '#245451' }}
                    >
                      View All
                    </Button>
                  </div>
                  <CardDescription style={{ color: '#64748B' }}>
                    Your latest cases and their status
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {stats.recentCases.length === 0 ? (
                    <div className="text-center py-8">
                      <FileText className="h-12 w-12 mx-auto mb-3" style={{ color: '#84a2ac' }} />
                      <p style={{ color: '#64748B' }}>No cases yet</p>
                      <p className="text-sm mt-1" style={{ color: '#84a2ac' }}>
                        Create your first case to get started
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {stats.recentCases.map((caseItem) => (
                        <div
                          key={caseItem.id}
                          onClick={() => navigate(`/case/${caseItem.id}`)}
                          className="flex items-center justify-between p-3 rounded-lg hover:shadow-md transition-all cursor-pointer"
                          style={{ backgroundColor: 'rgba(255, 255, 255, 0.5)' }}
                        >
                          <div className="flex-1">
                            <p className="font-medium mb-1" style={{ color: '#245451' }}>
                              {caseItem.case_name}
                            </p>
                            <div className="flex items-center gap-4 text-xs" style={{ color: '#64748B' }}>
                              <div className="flex items-center gap-1">
                                <User className="h-3 w-3" />
                                {caseItem.patient_name}
                              </div>
                              <div className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                {new Date(caseItem.date_of_injury).toLocaleDateString()}
                              </div>
                            </div>
                            <div className="flex items-center gap-1 text-xs mt-1" style={{ color: '#94a3b8' }}>
                              <Clock className="h-3 w-3" />
                              Created: {new Date(caseItem.created_at).toLocaleTimeString('en-US', { 
                                hour: 'numeric', 
                                minute: '2-digit',
                                hour12: true 
                              })}
                            </div>
                          </div>
                          <span 
                            className="px-3 py-1 rounded-full text-xs font-medium"
                            style={{
                              backgroundColor: caseItem.status === 'completed' ? '#D1F4E0' :
                                             caseItem.status === 'processing' ? '#FEF3C7' : '#F1F5F9',
                              color: caseItem.status === 'completed' ? '#245451' :
                                    caseItem.status === 'processing' ? '#854D0E' : '#475569'
                            }}
                          >
                            {caseItem.status}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Summaries Card */}
            <div className="grid gap-6 lg:grid-cols-3 mt-6">
              <Card className="glass-card hover:shadow-lg transition-all">
                <CardHeader>
                  <div className="flex items-center space-x-3 mb-2">
                    <div 
                      className="p-2 rounded-lg"
                      style={{ backgroundColor: 'rgba(64, 105, 98, 0.15)' }}
                    >
                      <FileText className="h-6 w-6" style={{ color: '#406962' }} />
                    </div>
                    <CardTitle style={{ color: '#245451' }}>Summaries Generated</CardTitle>
                  </div>
                  <CardDescription style={{ color: '#64748B' }}>
                    Total completed chronologies and billing summaries
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-4xl font-bold mb-2" style={{ color: '#245451' }}>
                    {stats.completedCases}
                  </div>
                  <Button 
                    variant="outline"
                    onClick={() => navigate('/summaries')}
                    className="w-full mt-4"
                    style={{ borderColor: '#E2E8F0', color: '#245451' }}
                  >
                    View All Summaries
                  </Button>
                </CardContent>
              </Card>

              <Card className="glass-card hover:shadow-lg transition-all lg:col-span-2">
                <CardHeader>
                  <div className="flex items-center space-x-3 mb-2">
                    <div 
                      className="p-2 rounded-lg"
                      style={{ backgroundColor: 'rgba(132, 162, 172, 0.15)' }}
                    >
                      <BarChart3 className="h-6 w-6" style={{ color: '#84a2ac' }} />
                    </div>
                    <CardTitle style={{ color: '#245451' }}>Quick Actions</CardTitle>
                  </div>
                  <CardDescription style={{ color: '#64748B' }}>
                    Common tasks and tools
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-3">
                    <Button 
                      variant="outline"
                      onClick={() => navigate('/upload')}
                      className="h-20 flex flex-col items-center justify-center gap-2"
                      style={{ borderColor: '#E2E8F0', color: '#245451' }}
                    >
                      <Upload className="h-5 w-5" />
                      <span className="text-sm">New Case</span>
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => navigate('/summaries')}
                      className="h-20 flex flex-col items-center justify-center gap-2"
                      style={{ borderColor: '#E2E8F0', color: '#245451' }}
                    >
                      <FileText className="h-5 w-5" />
                      <span className="text-sm">View Summaries</span>
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => navigate('/archives')}
                      className="h-20 flex flex-col items-center justify-center gap-2"
                      style={{ borderColor: '#E2E8F0', color: '#245451' }}
                    >
                      <FileText className="h-5 w-5" />
                      <span className="text-sm">Archives</span>
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => navigate('/settings')}
                      className="h-20 flex flex-col items-center justify-center gap-2"
                      style={{ borderColor: '#E2E8F0', color: '#245451' }}
                    >
                      <FileText className="h-5 w-5" />
                      <span className="text-sm">Settings</span>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default DashboardPage;
